import { useState, useCallback } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { safeMatchCount, findMatches, replaceInText, docxToPreviewText, docxReplaceInPlace, type ReplaceOptions } from "./utils";
import { AlertTriangle, Upload, X } from "lucide-react";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Finds every occurrence of a specific word or phrase and replaces it with something else, all at once. Works with pasted text, or upload a file directly — .txt, .json, .csv, .md, .html, .xml, and .docx are all supported, and the result downloads back in the same format you uploaded.`,
  how: `Paste your text, or upload a file, then enter what you want to find and what to replace it with. For an uploaded file, download the corrected version afterward instead of copying from the box.`,
  tip: `For a .docx file specifically, the replace happens inside the actual document so formatting is preserved — a phrase is found even when Word has split it across formatting changes; the replacement takes the formatting of where the match starts.`,
};

const PLAIN_TEXT_EXTENSIONS = ["txt", "json", "csv", "md", "html", "xml"];

function getExtension(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() || "";
}

/** For formats with real structure (JSON, XML), warns if a replacement
 * would break that structure -- rather than silently handing back a file
 * that looks fine but no longer parses. Doesn't block the edit; the
 * person may still want to see and fix it themselves. */
function checkStructureAfterReplace(ext: string, result: string): string | null {
  if (ext === "json") {
    try { JSON.parse(result); return null; }
    catch { return "Heads up: this edit looks like it broke the JSON's structure — check the result carefully before using it."; }
  }
  if (ext === "xml") {
    const doc = new DOMParser().parseFromString(result, "application/xml");
    if (doc.getElementsByTagName("parsererror").length > 0) {
      return "Heads up: this edit looks like it broke the XML's structure — check the result carefully before using it.";
    }
  }
  return null;
}

export default function FindReplace() {
  const [text, setText] = useState("");
  const [find, setFind] = useState("");
  const [replace, setReplace] = useState("");
  const [useRegex, setUseRegex] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [preserveCase, setPreserveCase] = useState(false);
  const [multiline, setMultiline] = useState(false);
  const [undoText, setUndoText] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDocx, setIsDocx] = useState(false);
  const [docxResult, setDocxResult] = useState<{ blob: Blob; replacementCount: number } | null>(null);
  const [processing, setProcessing] = useState(false);

  const opts: ReplaceOptions = { find, replace, useRegex, caseSensitive, wholeWord, preserveCase, multiline };
  const matches = isDocx ? null : safeMatchCount(opts, text);

  const handleFileUpload = useCallback(async (files: File[]) => {
    const f = files[0];
    if (!f) return;
    const ext = getExtension(f.name);

    if (ext === "docx") {
      try {
        const preview = await docxToPreviewText(f);
        setUploadedFile(f);
        setIsDocx(true);
        setText(preview);
        setDocxResult(null);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Couldn't read this .docx file");
      }
      return;
    }

    if (ext === "doc") {
      toast.error("The old .doc format isn't supported — only .docx. Save the file as .docx first (File > Save As in Word) and try again.");
      return;
    }

    if (!PLAIN_TEXT_EXTENSIONS.includes(ext)) {
      toast.error(`".${ext}" isn't a supported format. Try .txt, .json, .csv, .md, .html, .xml, or .docx.`);
      return;
    }

    const content = await f.text();
    setUploadedFile(f);
    setIsDocx(false);
    setDocxResult(null);
    setText(content);
  }, []);

  const clearFile = () => {
    setUploadedFile(null);
    setIsDocx(false);
    setDocxResult(null);
    setText("");
    setUndoText(null);
    setLastResult(null);
  };

  const undo = () => {
    if (undoText === null) return;
    setText(undoText);
    setUndoText(null);
    setLastResult("Last replace undone.");
    if (isDocx) setDocxResult(null);
  };

  const { logUsageNow } = useLogUsageOnAction();

  const doReplace = async () => {
    if (!find) return;

    // Replace All on text typed or pasted into the box is this tool's real
    // action and was completely ungated -- QA found it rewriting text past
    // the daily limit (only the final Download was checked). A file that was
    // uploaded takes its use at Download (gateAtDownload), so that path is
    // not counted here as well.
    if (!uploadedFile) {
      const allowed = await logUsageNow();
      if (!allowed) return;
    }

    if (isDocx && uploadedFile) {
      setProcessing(true);
      try {
        const result = await docxReplaceInPlace(uploadedFile, opts);
        setUndoText(text);
        setText(result.previewText);
        setLastResult(result.replacementCount > 0 ? `Replaced ${result.replacementCount} occurrence${result.replacementCount === 1 ? "" : "s"}.` : "No matches found in this document.");
        setDocxResult({ blob: result.blob, replacementCount: result.replacementCount });
        toast.success(result.replacementCount > 0 ? `Replaced ${result.replacementCount} occurrence(s)!` : "No matches found in this document.");
      } catch {
        toast.error("That pattern isn't valid — check your regex syntax and try again.");
      } finally {
        setProcessing(false);
      }
      return;
    }

    try {
      const count = findMatches(opts, text).length;
      const result = replaceInText(opts, text);
      setUndoText(text);
      setText(result);
      setLastResult(count > 0 ? `Replaced ${count} occurrence${count === 1 ? "" : "s"}.` : "No matches found — nothing was changed.");
      if (count > 0) toast.success(`Replaced ${count} occurrence${count === 1 ? "" : "s"}`);
      if (uploadedFile) {
        const warning = checkStructureAfterReplace(getExtension(uploadedFile.name), result);
        if (warning) toast.warning(warning);
      }
    } catch {
      toast.error("That pattern isn't valid — check your regex syntax (an unclosed bracket or parenthesis is the usual cause) and try again.");
    }
  };

  const downloadResult = () => {
    if (!uploadedFile) return;
    if (isDocx && docxResult) {
      downloadBlob(docxResult.blob, uploadedFile.name.replace(/\.docx$/i, "_edited.docx"));
    } else {
      const ext = getExtension(uploadedFile.name);
      const mimeType = ext === "json" ? "application/json" : ext === "html" ? "text/html" : ext === "xml" ? "application/xml" : ext === "csv" ? "text/csv" : ext === "md" ? "text/markdown" : "text/plain";
      downloadBlob(new Blob([text], { type: mimeType }), uploadedFile.name.replace(/\.[^.]+$/, `_edited.${ext}`));
    }
  };

  return (
    <ToolContainer>
      {!uploadedFile && (
        <FileDropZone
          gateAtDownload
          accept=".txt,.json,.csv,.md,.html,.xml,.docx"
          onFiles={handleFileUpload}
          label="Drop a file (optional)"
          description=".txt, .json, .csv, .md, .html, .xml, or .docx — or just paste text below instead"
        />
      )}

      {uploadedFile && (
        <div className="flex items-center justify-between p-3 rounded-lg border bg-card mb-3">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-medium">{uploadedFile.name}</p>
            {isDocx && <span className="text-xs text-muted-foreground">(preview — formatting preserved on download)</span>}
          </div>
          <Button variant="ghost" size="icon" onClick={clearFile} aria-label="Remove file"><X className="h-4 w-4" /></Button>
        </div>
      )}

      {isDocx && (
        <NoticeBox className="mb-3">
          <div className="flex gap-2">
            <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs">
              This is a read-only preview of the document's text. Formatting, images, and layout are preserved and
              aren't shown here — edit using Find/Replace below, then download the corrected .docx.
            </p>
          </div>
        </NoticeBox>
      )}

      <Textarea
        aria-label="Text to search"
        placeholder="Enter your text..."
        value={text}
        onChange={e => !isDocx && setText(e.target.value)}
        readOnly={isDocx}
        className={`min-h-[150px] ${isDocx ? "bg-muted/30" : ""}`}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div><Label htmlFor="fr-find">Find</Label><Input id="fr-find" value={find} onChange={e => setFind(e.target.value)} placeholder="Search text..." aria-invalid={matches === "invalid"} aria-describedby={matches === "invalid" ? "fr-find-err" : undefined} /></div>
        <div><Label htmlFor="fr-replace">Replace with</Label><Input id="fr-replace" value={replace} onChange={e => setReplace(e.target.value)} placeholder="Replacement..." /></div>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3">
        <div className="flex items-center gap-2"><Switch checked={useRegex} onCheckedChange={setUseRegex} aria-label="Regex" /><Label className="text-sm">Regex</Label></div>
        <div className="flex items-center gap-2"><Switch checked={caseSensitive} onCheckedChange={setCaseSensitive} aria-label="Case sensitive" /><Label className="text-sm">Case sensitive</Label></div>
        <div className="flex items-center gap-2"><Switch checked={wholeWord} onCheckedChange={setWholeWord} aria-label="Whole word" /><Label className="text-sm">Whole word</Label></div>
        <div className="flex items-center gap-2"><Switch checked={preserveCase} onCheckedChange={setPreserveCase} aria-label="Preserve case" /><Label className="text-sm">Preserve case</Label></div>
        {useRegex && <div className="flex items-center gap-2"><Switch checked={multiline} onCheckedChange={setMultiline} aria-label="^ $ match each line" /><Label className="text-sm">^ $ match each line</Label></div>}
        {find && !isDocx && (
          <span id={matches === "invalid" ? "fr-find-err" : undefined} role={matches === "invalid" ? "alert" : undefined} className={`text-sm ${matches === "invalid" ? "text-destructive font-medium" : "text-muted-foreground"}`}>
            {matches === "invalid" ? "Invalid pattern — check your regex syntax" : `${matches} match${matches === 1 ? "" : "es"} found`}
          </span>
        )}
      </div>
      <div className="flex gap-3 mt-4">
        <Button onClick={doReplace} disabled={!find || matches === "invalid" || processing}>
          {processing ? "Replacing..." : "Replace All"}
        </Button>
        {undoText !== null && <Button variant="outline" onClick={undo}>Undo</Button>}
        {uploadedFile && (isDocx ? docxResult : true) && (
          <DownloadButton onClick={downloadResult} label={isDocx ? "Download Corrected .docx" : "Download Corrected File"} />
        )}
      </div>
      {lastResult && <p className="text-sm mt-3" role="status" data-testid="fr-result">{lastResult}</p>}
      {useRegex && <p className="text-xs text-muted-foreground mt-2">Regex mode: use $1, $&amp; or $&lt;name&gt; in the replacement, and \n or \t for a new line or tab.</p>}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
